import { cn } from '../cn'
import { Avatar, AvatarFallback, AvatarImage } from './avatar'

interface UserAvatarProps {
  /** User's profile image URL (if provided, takes precedence) */
  image?: string | null
  /** User's name (used for initials + gradient generation) */
  name?: string | null
  /** User's email (fallback if no name) */
  email?: string | null
  /** Size of the avatar */
  size?: 'xs' | 'sm' | 'md' | 'lg'
  /** Additional class names */
  className?: string
}

const sizeClasses = {
  xs: 'h-4 w-4',
  sm: 'h-5 w-5',
  md: 'h-8 w-8',
  lg: 'h-10 w-10',
}

const textSizeClasses = {
  xs: 'text-[8px]',
  sm: 'text-[10px]',
  md: 'text-xs',
  lg: 'text-sm',
}

// Split a name into word tokens, treating "." / "_" / "-" as separators (not
// just whitespace). The same person is frequently stored one way in one record
// and another elsewhere — e.g. an auth `User.name` of "Landry.D" vs an HR
// `Employee` "Landry D" — and keying initials/color off the raw string would
// then render that one person with two different avatars across surfaces.
function nameTokens(name: string): string[] {
  return name
    .trim()
    .split(/[\s._-]+/)
    .filter(Boolean)
}

export function getUserInitials({
  name,
  email,
}: {
  name?: string | null
  email?: string | null
}) {
  const trimmedName = name?.trim()
  if (trimmedName) {
    const words = nameTokens(trimmedName)
    if (words.length >= 2) {
      return (words[0][0] + words[words.length - 1][0]).toUpperCase()
    }
    return trimmedName.replace(/[._-]+/g, '').slice(0, 2).toUpperCase()
  }
  return (email?.slice(0, 2) || 'U').toUpperCase()
}

// Curated duotone pairs — every combination stays vivid and keeps white
// initials legible. Hue-math gradients were dropped because random pairs
// drift into muddy olive/brown territory.
const AVATAR_GRADIENTS: ReadonlyArray<readonly [string, string]> = [
  ['#6E8BFF', '#3F5FCE'], // cobalt (brand accent family)
  ['#8B5CF6', '#4F46E5'], // violet → indigo
  ['#D946EF', '#9333EA'], // fuchsia → purple
  ['#FB7185', '#E11D48'], // rose
  ['#FB923C', '#EA580C'], // orange
  ['#F97316', '#DB2777'], // orange → pink
  ['#34D399', '#059669'], // emerald
  ['#2DD4BF', '#0D9488'], // teal
  ['#22D3EE', '#0284C7'], // cyan → blue
  ['#38BDF8', '#4338CA'], // sky → indigo
  ['#F472B6', '#BE185D'], // pink → deep rose
  ['#A78BFA', '#2563EB'], // lavender → blue
  ['#4ADE80', '#0891B2'], // green → cyan
  ['#60A5FA', '#7C3AED'], // blue → violet
  ['#F87171', '#DC2626'], // red
  ['#FBBF24', '#EA580C'], // amber → orange
]

/**
 * Stable per-user gradient key. Name comes first because it is the one field
 * every avatar call site has (it's displayed beside the avatar), while email
 * is missing from several DTOs — seeding by email would give the same user
 * different colors on different pages.
 */
export function getAvatarSeed({
  name,
  email,
}: {
  name?: string | null
  email?: string | null
}) {
  const trimmedName = name?.trim()
  // Seed off the same separator-normalized tokens as the initials, so
  // "Landry.D" and "Landry D" hash to one colour for the same person.
  if (trimmedName) return nameTokens(trimmedName).join(' ').toLowerCase()
  return (email?.trim() || 'user').toLowerCase()
}

/**
 * Deterministic gradient derived from the user's name/email.
 * Generated locally (previously avatar.vercel.sh): no network round-trip,
 * works offline, and the seed never leaves the app.
 */
function getAvatarColors(seed: string) {
  let hash = 0
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0
  }
  return AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length]
}

export function getAvatarGradient(seed: string) {
  const [from, to] = getAvatarColors(seed)
  return `linear-gradient(135deg, ${from}, ${to})`
}

/** Stable solid color from the same palette used by gradient avatars. */
export function getAvatarColor(seed: string) {
  return getAvatarColors(seed)[0]
}

/**
 * User avatar: uploaded image when present, otherwise a locally generated
 * gradient with the user's initials.
 */
export function UserAvatar({
  image,
  name,
  email,
  size = 'sm',
  className,
}: UserAvatarProps) {
  const displayName = name || email || ''
  const seed = getAvatarSeed({ name, email })

  return (
    <Avatar
      className={cn(
        // 1px ring at the avatar's edge gives every user avatar a
        // consistent silhouette on card surfaces. `bg-muted` fills the
        // circle behind the image so transparent-PNG / SVG avatars (e.g.
        // service-account leaf marks) don't read as non-circular.
        'bg-muted ring-1 ring-border',
        sizeClasses[size],
        className
      )}
    >
      {image ? (
        <>
          <AvatarImage src={image} alt={displayName || 'User'} />
          <AvatarFallback delayMs={500} className={cn(textSizeClasses[size])}>
            {getUserInitials({ name, email })}
          </AvatarFallback>
        </>
      ) : (
        <span
          className='flex h-full w-full items-center justify-center'
          style={{ background: getAvatarGradient(seed || 'user') }}
        >
          {/* SVG text scales with the avatar, keeping initials proportional
              at any rendered size (including className size overrides).
              `size-full` (not `h-full w-full`) is load-bearing: menu/command
              items and buttons style descendant svgs with
              [&_svg:not([class*='size-'])]:size-4, which would otherwise
              shrink the initials to 16px inside dropdowns. */}
          <svg className='size-full' viewBox='0 0 100 100' aria-hidden='true'>
            <text
              x='50'
              y='50'
              textAnchor='middle'
              dominantBaseline='central'
              fill='#fff'
              fontFamily='sans-serif'
              fontSize='40'
              fontWeight='500'
            >
              {getUserInitials({ name, email })}
            </text>
          </svg>
        </span>
      )}
    </Avatar>
  )
}
